import { FC, useMemo } from "react";
import { getSegmentNameFromRange } from "../../../Workspace/View/MarketLandscape/utils";
import { pluralize } from "../../../../utils/text";
import { ICPDistributionChart } from "./ICPDistributionChart";
import { DEFAULT_API_REVENUE_RANGES } from "../../../Workspace/View/constants";

interface RevenueDistributionItem {
  share: number;
  count: number;
  isSignificant: boolean;
  revenueRange: string;
  revenueRangeMax: number | null;
  revenueRangeMin: number | null;
}

interface ICPRevenueChartProps {
  /** Revenue distribution data from API */
  data: RevenueDistributionItem[];
  /** Height of the chart container */
  height?: string;
  /** Whether the chart is loading */
  isLoading?: boolean;
}

/**
 * Component that displays revenue distribution chart with proper revenue range formatting
 */
export const ICPRevenueChart: FC<ICPRevenueChartProps> = ({
  data,
  height,
  isLoading = false,
}) => {
  const chartData = useMemo(() => {
    // First map the data to include the category
    const mappedData = data.map((item) => {
      const range = {
        max: item.revenueRangeMax || Number.POSITIVE_INFINITY,
        min: item.revenueRangeMin || 0,
      };

      return {
        category: getSegmentNameFromRange(range, DEFAULT_API_REVENUE_RANGES),
        value: item.share,
        count: item.count,
        countText: pluralize("company", item.count),
        // Store the min value for sorting
        minValue: item.revenueRangeMin || 0,
      };
    });

    // Sort by the minimum value of each range
    return mappedData.sort((a, b) => a.minValue - b.minValue);
  }, [data]);

  const totalCount = useMemo(() => {
    return data.reduce((sum, item) => sum + item.count, 0);
  }, [data]);

  return (
    <ICPDistributionChart
      title="Revenue Distribution"
      data={chartData}
      height={height}
      columnLabelFormat="{valueY}% ({count})"
      columnTooltipFormat="{categoryX}: [bold]{valueY}%[/] ({count} {countText})"
      totalCount={totalCount}
      isLoading={isLoading}
    />
  );
};
